import React, { useEffect, useRef, useState } from 'react';
import { ScheduleHoverDetails, HoverStaffDetail } from './boardViewHelpers.ts';
import { utcToLocalTime } from '../utils/timezone.ts';

interface BoardScheduleBlockProps {
  /** Text shown inside the block (the surgery type). */
  label: string;
  /** Full className for the block, e.g. "schedule-block clickable success". */
  className: string;
  /** Absolute-positioning style for the block within the timeline row. */
  style: React.CSSProperties;
  /** Accessible label / native tooltip fallback. */
  ariaLabel: string;
  onClick: (e: React.MouseEvent) => void;
  /** Pre-computed staff details surfaced on hover. */
  details: ScheduleHoverDetails;
  /** When true, render the small corner "⚑" button that opens the case-markers popover (admin only). */
  showFlag?: boolean;
  /** Keeps the flag button visible without hover (used when the case already has a hand-set marker). */
  flagActive?: boolean;
  /** Opens the case-markers popover, anchored to this block's on-screen rect. */
  onFlagClick?: (rect: DOMRect) => void;
  /** True when this schedule has a manual pin staged for the next Allocate run. Shows a small corner
   *  marker so the pin stays visible WITHOUT recoloring the whole block (staffing color wins). */
  pinned?: boolean;
}

const TOOLTIP_WIDTH = 300;
const ESTIMATED_TOOLTIP_HEIGHT = 260;
const VIEWPORT_MARGIN = 12;
// Small delay so quickly skimming the mouse across a dense board does not flash
// a tooltip on every block it passes over.
const OPEN_DELAY_MS = 140;

const formatShift = (startUtc?: string, endUtc?: string): string | null => {
  if (!startUtc || !endUtc) return null;
  return `${utcToLocalTime(startUtc)} – ${utcToLocalTime(endUtc)}`;
};

/** A single doctor / nurse row inside the hover tooltip. */
// Anesthesia-specific labels for the hover (scoped here; does not change labels elsewhere).
const HOVER_TYPE_LABEL: Record<string, string> = {
  doctor: 'Anesthesiologist',
  nurse: 'CRNA',
  // RRNA/SRNA are interchangeable — both student types just read as "SRNA".
  'nurse-student-junior': 'SRNA',
  'nurse-student-senior': 'SRNA',
};
const hoverTypeLabel = (employeeType: string, fallback = ''): string => HOVER_TYPE_LABEL[employeeType] || fallback;

const StaffRow: React.FC<{ staff: HoverStaffDetail }> = ({ staff }) => {
  const shiftRange = formatShift(staff.shiftStartUtc, staff.shiftEndUtc);
  const typeLabel = hoverTypeLabel(staff.employeeType, staff.typeLabel);
  return (
    <li className={`schedule-hover-staff ${staff.isDoctor ? 'doctor' : 'nurse'}`}>
      <div className="schedule-hover-staff-top">
        <span className="schedule-hover-staff-name">{staff.name}</span>
        {staff.order !== null ? (
          <span className="schedule-hover-badge schedule-hover-badge--order">Pecking #{staff.order}</span>
        ) : staff.isShiftDoctor ? (
          <span className="schedule-hover-badge schedule-hover-badge--shift-doctor">Shift Doctor</span>
        ) : typeLabel ? (
          <span className="schedule-hover-badge schedule-hover-badge--type">{typeLabel}</span>
        ) : null}
      </div>
      <div className="schedule-hover-staff-meta">
        {shiftRange ? <span>Shift: {shiftRange}</span> : <span className="schedule-hover-muted">No shift on file</span>}
        {staff.homeHospitalName && <span> · Home: {staff.homeHospitalName}</span>}
      </div>
    </li>
  );
};

const BoardScheduleBlock: React.FC<BoardScheduleBlockProps> = ({
  label,
  className,
  style,
  ariaLabel,
  onClick,
  details,
  showFlag,
  flagActive,
  onFlagClick,
  pinned,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);

  const clearTimer = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  const computePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    // Prefer anchoring just below the block's left edge, then keep it on-screen.
    let left = rect.left;
    if (left + TOOLTIP_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN);
    }

    let top = rect.bottom + 8;
    if (top + ESTIMATED_TOOLTIP_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
      // Not enough room below — flip above the block.
      top = Math.max(VIEWPORT_MARGIN, rect.top - ESTIMATED_TOOLTIP_HEIGHT - 8);
    }

    setPosition({ top, left });
  };

  const handleEnter = () => {
    clearTimer();
    openTimer.current = window.setTimeout(() => {
      computePosition();
      setOpen(true);
    }, OPEN_DELAY_MS);
  };

  const handleLeave = () => {
    clearTimer();
    setOpen(false);
  };

  useEffect(() => clearTimer, []);

  const shiftRange = formatShift(details.startTimeUtc, details.endTimeUtc);

  return (
    <>
      <div
        ref={triggerRef}
        className={className}
        style={style}
        aria-label={ariaLabel}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={onClick}
      >
        <div className="schedule-type">{label}</div>
        {pinned && (
          // Small "pinned" dot in the top-left corner — the room was hand-edited and will be kept fixed
          // on the next Allocate run. Kept separate from the block color so staffing (green/red) still reads.
          <span
            aria-label="pinned for next allocation"
            title="Pinned — kept fixed on the next Allocate run"
            style={{
              position: 'absolute', top: '2px', left: '2px', width: '8px', height: '8px', borderRadius: '50%',
              background: 'var(--or-blue, #1a7abf)', border: '1px solid rgba(255,255,255,0.85)',
              boxShadow: '0 0 3px rgba(0,0,0,0.4)', pointerEvents: 'none', zIndex: 3,
            }}
          />
        )}
        {showFlag && (
          <button
            type="button"
            className={`case-flag-btn${flagActive ? ' active' : ''}`}
            title="Mark this case — nerve block / pediatric"
            aria-label="Mark this case — nerve block or pediatric"
            draggable={false}
            onClick={(e) => {
              e.stopPropagation(); // don't open the staffing window / timeline handler
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect && onFlagClick) onFlagClick(rect);
            }}
          >⚑</button>
        )}
      </div>

      {open && (
        <div
          role="tooltip"
          className="schedule-hover-tooltip"
          style={{ position: 'fixed', top: `${position.top}px`, left: `${position.left}px`, width: `${TOOLTIP_WIDTH}px` }}
        >
          <div className="schedule-hover-header">
            <div className="schedule-hover-title">{details.surgeryType}</div>
            {shiftRange && <div className="schedule-hover-subtitle">{shiftRange}</div>}
          </div>

          {details.totalStaff === 0 ? (
            <div className="schedule-hover-empty">
              {details.source === 'none'
                ? 'No staff allocated yet.'
                : 'No staff allocated to this schedule.'}
            </div>
          ) : (
            <div className="schedule-hover-sections">
              {details.source === 'manual' && (
                <div className="schedule-hover-source">Pending manual allocation</div>
              )}

              {details.doctors.length > 0 && (
                <div className="schedule-hover-section">
                  <div className="schedule-hover-section-title">Anesthesiologists ({details.doctors.length})</div>
                  <ul className="schedule-hover-list">
                    {details.doctors.map((d) => (
                      <StaffRow key={d.employeeId} staff={d} />
                    ))}
                  </ul>
                </div>
              )}

              {details.nurses.length > 0 && (
                <div className="schedule-hover-section">
                  <div className="schedule-hover-section-title">
                    {/* Title reflects who's actually staffed: CRNA, RRNA/SRNA, or a mix. */}
                    {(Array.from(new Set(details.nurses.map(n => hoverTypeLabel(n.employeeType, n.typeLabel)).filter(Boolean))).join(' / ') || 'CRNA')} ({details.nurses.length})
                  </div>
                  <ul className="schedule-hover-list">
                    {details.nurses.map((n) => (
                      <StaffRow key={n.employeeId} staff={n} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BoardScheduleBlock;
